/**
 * TimeOffPagination Component
 * Handles pagination display and navigation
 * Memoized to prevent unnecessary re-renders
 */

import React, { memo, useMemo, useCallback } from "react";

const TimeOffPagination = memo(({
  currentPage,
  totalPages,
  perPage,
  total,
  dataLength,
  onPageChange,
  onPerPageChange,
}) => {
  // Memoize page numbers array
  const pageNumbers = useMemo(() => {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }, [totalPages]);

  // Memoize handlers
  const handlePageChange = useCallback((pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      onPageChange(pageNumber);
    }
  }, [totalPages, onPageChange]);

  const handlePerPageChange = useCallback((newPerPage) => {
    onPerPageChange(newPerPage);
    handlePageChange(1);
  }, [onPerPageChange, handlePageChange]);

  if (!dataLength || dataLength === 0) {
    return null;
  }

  return (
    <>
      {/* Per Page Selector */}
      <div className="showing-count showing-count-timeoff">
        <div className="filter-inner showing-result show-result-team-fill">
          <label>Showing</label>
          <div className="select-modal-mainM custom-dropdown-user-new-outer">
            <div className="selected-option custom-dropdown-user-new">
              {total < 10 ? total.toString() : perPage.toString()}
            </div>
            <div className="custom-dropdown-new">
              <ul className="showing-options-list">
                {total >= 10 && (
                  <li
                    className={perPage === 10 ? "selected-item" : ""}
                    onClick={() => handlePerPageChange(10)}
                  >
                    10
                  </li>
                )}
                {total >= 20 && (
                  <li
                    className={perPage === 20 ? "selected-item" : ""}
                    onClick={() => handlePerPageChange(20)}
                  >
                    20
                  </li>
                )}
                {total >= 30 && (
                  <li
                    className={perPage === 30 ? "selected-item" : ""}
                    onClick={() => handlePerPageChange(30)}
                  >
                    30
                  </li>
                )}
                {total >= 40 && (
                  <li
                    className={perPage === 40 ? "selected-item" : ""}
                    onClick={() => handlePerPageChange(40)}
                  >
                    40
                  </li>
                )}
                {total >= 10 && (
                  <li
                    className={perPage === total ? "selected-item" : ""}
                    onClick={() => handlePerPageChange(total)}
                  >
                    ALL
                  </li>
                )}
                {total < 10 && (
                  <li
                    className={perPage === total ? "selected-item" : ""}
                    onClick={() => handlePerPageChange(total)}
                  >
                    {total}
                  </li>
                )}
              </ul>
            </div>
          </div>
          <label>of {total} Results</label>
        </div>
      </div>

      {/* Pagination Controls */}
      {dataLength > 0 && (
        <ul className="pagination">
          <li className="back">
            <button
              className={currentPage === 1 ? "disabled" : ""}
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              Back
            </button>
          </li>
          {pageNumbers.map((page) => (
            <li key={page} className={currentPage === page ? "active" : ""}>
              <button onClick={() => handlePageChange(page)}>{page}</button>
            </li>
          ))}
          <li className="next">
            <button
              className={currentPage === totalPages ? "disabled" : ""}
              disabled={currentPage === totalPages || dataLength < perPage}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              Next
            </button>
          </li>
        </ul>
      )}
    </>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  return (
    prevProps.currentPage === nextProps.currentPage &&
    prevProps.totalPages === nextProps.totalPages &&
    prevProps.perPage === nextProps.perPage &&
    prevProps.total === nextProps.total &&
    prevProps.dataLength === nextProps.dataLength &&
    prevProps.onPageChange === nextProps.onPageChange &&
    prevProps.onPerPageChange === nextProps.onPerPageChange
  );
});

TimeOffPagination.displayName = "TimeOffPagination";

export default TimeOffPagination;
